import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE } from '../../config';

// ============================================================
// Pipeline Toast — Global floating notification for orchestrator status
// Visible on every page when the pipeline is running or recently completed.
// Positioned top-right. Minimizable to a single-line indicator.
// ============================================================

interface OrchestratorStatus {
  is_running: boolean;
  running_stewards: Record<string, string>;
  configured_stewards: string[];
  parallel_limit: number;
  staleness_hours: number;
  last_collection?: {
    status: string;
    ndcs?: number;
    stewards_dispatched?: number;
    stewards_succeeded?: number;
    stewards_failed?: number;
    evidence_collected?: number;
    duration_seconds?: number;
    completed_at?: string;
    steward_results?: Record<string, { status: string; error?: string | null }>;
  };
}

type ToastState = 'hidden' | 'running' | 'completed';

export default function PipelineToast() {
  const [status, setStatus] = useState<OrchestratorStatus | null>(null);
  const [toastState, setToastState] = useState<ToastState>('hidden');
  const [minimized, setMinimized] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const prevRunning = useRef(false);
  const completedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/metasupply/swarm/orchestrator/status`, { credentials: 'include' });
      if (resp.ok) {
        const data: OrchestratorStatus = await resp.json();
        setStatus(data);

        const isRunning = data.is_running;

        if (isRunning) {
          // Pipeline started or still running
          setToastState('running');
          setDismissed(false);
          setMinimized(false);
          prevRunning.current = true;
          // Clear any completed timer
          if (completedTimer.current) {
            clearTimeout(completedTimer.current);
            completedTimer.current = null;
          }
        } else if (prevRunning.current && !isRunning) {
          // Pipeline just completed
          prevRunning.current = false;
          setToastState('completed');
          setMinimized(false);
          // Auto-dismiss after 30 seconds
          completedTimer.current = setTimeout(() => {
            setToastState('hidden');
            setDismissed(false);
          }, 30000);
        }
        // If it was never running, keep hidden
      }
    } catch {
      // Non-critical — toast just won't show
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    // Poll every 10s when running, every 30s otherwise (reduced from 3s/15s to avoid server load)
    const interval = setInterval(fetchStatus, toastState === 'running' ? 10000 : 30000);
    return () => clearInterval(interval);
  }, [fetchStatus, toastState]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (completedTimer.current) clearTimeout(completedTimer.current);
    };
  }, []);

  // Don't render if hidden or dismissed
  if (toastState === 'hidden' || dismissed || !status) return null;

  const stewards = status.running_stewards || {};
  const stewardEntries = Object.entries(stewards);
  const completedCount = stewardEntries.filter(([, s]) => s === 'completed').length;
  const failedCount = stewardEntries.filter(([, s]) => s === 'failed').length;
  const totalCount = stewardEntries.length || (status.configured_stewards?.length ?? 0);
  const runningCount = stewardEntries.filter(([, s]) => s === 'running').length;
  const progressPct = totalCount > 0 ? Math.round(((completedCount + failedCount) / totalCount) * 100) : 0;

  // Completed state uses last_collection data
  const lastCollection = status.last_collection;
  const lastCompleted = lastCollection?.stewards_succeeded ?? 0;
  const lastFailed = lastCollection?.stewards_failed ?? 0;
  const lastTotal = lastCollection?.stewards_dispatched ?? (status.configured_stewards?.length ?? 0);
  const lastDuration = lastCollection?.duration_seconds;
  const lastEvidence = lastCollection?.evidence_collected ?? 0;

  const handleDismiss = () => {
    setDismissed(true);
    if (completedTimer.current) {
      clearTimeout(completedTimer.current);
      completedTimer.current = null;
    }
  };

  // ── Minimized view ──
  if (minimized) {
    return (
      <div
        onClick={() => setMinimized(false)}
        style={{
          position: 'fixed', top: 76, right: 16, zIndex: 9999,
          background: toastState === 'running' ? '#1e293b' : '#0f4a2e',
          color: '#fff', borderRadius: 8, padding: '10px 14px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 8,
          fontFamily: "'DM Sans', -apple-system, sans-serif",
          transition: 'all 0.3s ease',
        }}
      >
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: toastState === 'running' ? '#3b82f6' : '#22c55e',
          animation: toastState === 'running' ? 'pipelinePulse 1.5s infinite' : 'none',
        }} />
        <span style={{ fontSize: 11, fontWeight: 600 }}>
          {toastState === 'running' ? 'Pipeline Running' : 'Pipeline Complete'}
        </span>
        <span style={{ fontSize: 10, color: '#94a3b8' }}>
          {toastState === 'running' ? `${completedCount}/${totalCount}` : `${lastCompleted}/${lastTotal}`}
        </span>
        <style>{`@keyframes pipelinePulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>
      </div>
    );
  }

  // ── Full expanded view ──
  return (
    <div style={{
      position: 'fixed', top: 76, right: 16, zIndex: 9999,
      width: 320, background: '#1e293b', color: '#fff',
      borderRadius: 10, overflow: 'hidden',
      boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
      fontFamily: "'DM Sans', -apple-system, sans-serif",
      transition: 'all 0.3s ease',
    }}>
      <style>{`@keyframes pipelinePulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 14px', borderBottom: '1px solid #334155',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%',
            background: toastState === 'running' ? '#3b82f6' : '#22c55e',
            animation: toastState === 'running' ? 'pipelinePulse 1.5s infinite' : 'none',
          }} />
          <span style={{ fontSize: 12, fontWeight: 600 }}>Pipeline Queue</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button
            onClick={() => setMinimized(true)}
            title="Minimize"
            style={{
              background: 'none', border: 'none', color: '#64748b', cursor: 'pointer',
              fontSize: 16, padding: '2px 6px', lineHeight: 1,
            }}
          >
            &mdash;
          </button>
          <button
            onClick={handleDismiss}
            title="Dismiss"
            style={{
              background: 'none', border: 'none', color: '#64748b', cursor: 'pointer',
              fontSize: 16, padding: '2px 6px', lineHeight: 1,
            }}
          >
            &times;
          </button>
        </div>
      </div>

      {/* Body */}
      {toastState === 'running' ? (
        <>
          <div style={{ padding: '10px 14px', maxHeight: 180, overflowY: 'auto' }}>
            <div style={{
              fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#64748b',
              marginBottom: 6, letterSpacing: '0.05em',
            }}>
              Collect ({completedCount + failedCount}/{totalCount})
            </div>
            {stewardEntries.map(([sid, sstatus]) => (
              <div key={sid} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                fontSize: 11, marginBottom: 3, padding: '2px 0',
              }}>
                <div style={{
                  width: 5, height: 5, borderRadius: '50%', flexShrink: 0,
                  background: sstatus === 'completed' ? '#4ade80'
                    : sstatus === 'running' ? '#60a5fa'
                    : sstatus === 'failed' ? '#ef4444'
                    : '#64748b',
                  animation: sstatus === 'running' ? 'pipelinePulse 1.5s infinite' : 'none',
                }} />
                <span style={{ flex: 1, color: '#cbd5e1' }}>{sid.replace('steward_', '')}</span>
                <span style={{ color: '#64748b', fontSize: 10 }}>
                  {sstatus === 'completed' ? 'done' : sstatus === 'running' ? 'running...' : sstatus}
                </span>
              </div>
            ))}
            {stewardEntries.length === 0 && (
              <div style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>Preparing stewards...</div>
            )}
          </div>

          {/* Scoring agents — queued */}
          {runningCount > 0 && (
            <div style={{ padding: '0 14px 6px' }}>
              <div style={{
                fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#64748b',
                marginBottom: 4, letterSpacing: '0.05em',
              }}>
                Score (queued)
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, padding: '2px 0' }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#475569' }} />
                <span style={{ color: '#64748b' }}>Scoring agents waiting...</span>
              </div>
            </div>
          )}

          {/* Progress bar */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 14px', borderTop: '1px solid #334155', background: '#1a2332',
          }}>
            <div style={{ flex: 1, marginRight: 10 }}>
              <div style={{ height: 3, background: '#334155', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', background: '#3b82f6', borderRadius: 2,
                  width: `${progressPct}%`, transition: 'width 0.5s ease',
                }} />
              </div>
            </div>
            <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>
              {progressPct}%
            </span>
          </div>
        </>
      ) : (
        /* Completed state */
        <div style={{ padding: '14px' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5">
              <path d="M20 6L9 17l-5-5" />
            </svg>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#4ade80' }}>Pipeline Complete</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#4ade80' }}>{lastCompleted}</div>
              <div style={{ fontSize: 9, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Done</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: lastFailed > 0 ? '#ef4444' : '#64748b' }}>{lastFailed}</div>
              <div style={{ fontSize: 9, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Failed</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#60a5fa' }}>{lastEvidence}</div>
              <div style={{ fontSize: 9, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Evidence</div>
            </div>
          </div>

          {lastDuration != null && (
            <div style={{ fontSize: 10, color: '#64748b', textAlign: 'center' }}>
              Completed in {lastDuration.toFixed(1)}s
            </div>
          )}
        </div>
      )}
    </div>
  );
}
